import { useState, useEffect } from "react";
import { useBreadcrumbs } from "../context/BreadcrumbContext";
import { cn } from "../lib/utils";

// ─── Types ────────────────────────────────────────────────────────────────────

interface TechBlock {
  type: string;
  name: string;
  file?: string;
  description: string;
}

interface Step {
  num: number;
  title: string;
  operatorView: string;
  technical: TechBlock[];
}

interface FlowContent {
  label: string;
  agentType?: string;
  goal: string;
  steps: Step[];
}

type LayerType =
  | "frontend"
  | "backend"
  | "database"
  | "analytics"
  | "ai"
  | "automation"
  | "auth"
  | "infra";

// ─── Left-nav sections ────────────────────────────────────────────────────────

const NAV_SECTIONS = [
  {
    label: "PATIENT JOURNEY PIPELINE",
    items: [
      { id: "pipeline", label: "Two-Phase Pipeline" },
      { id: "stage-2", label: "Stage 2 — Initial Appointments" },
      { id: "stage-3", label: "Stage 3 — Ultrasound Review" },
      { id: "stage-5", label: "Stage 5 — Procedure/Ablation" },
    ],
  },
  {
    label: "REMEDIATION PATH",
    items: [
      { id: "triage-router", label: "Triage Router" },
      { id: "ops-supervisor", label: "Ops Supervisor" },
      { id: "prior-auth", label: "Prior Auth Agent" },
    ],
  },
  {
    label: "PLATFORM AGENTS",
    items: [
      { id: "agent-architect", label: "Agent Architect" },
      { id: "data-centralizer", label: "Data Centralizer" },
      { id: "upgrade-agent", label: "Upgrade Agent" },
      { id: "memory-audit", label: "Memory Audit Agent" },
      { id: "model-optimizer", label: "Model Optimizer" },
    ],
  },
  {
    label: "AUTOMATION",
    items: [
      { id: "looker-scanner", label: "Looker KPI Scanner" },
      { id: "metric-runner", label: "MetricMonitorRunner" },
    ],
  },
];

// ─── Flow content ─────────────────────────────────────────────────────────────

const FLOWS: Record<string, FlowContent> = {
  pipeline: {
    label: "Two-Phase Pipeline",
    goal: "The Patient Journey Monitor runs on a 30-minute interval via MetricMonitorRunner. Phase 1 is fully deterministic — zero Bedrock calls on healthy runs. Phase 2 fires Claude Haiku only when a KPI crosses its threshold, creating a PjEscalation and routing to the remediation path.",
    steps: [
      {
        num: 1,
        title: "MetricMonitorRunner fires (every 30 min)",
        operatorView:
          "The platform checks all patient journey KPIs on a fixed schedule without any AI involvement. Runs automatically in the background on the ECS container.",
        technical: [
          {
            type: "NESTJS SERVICE",
            name: "MetricMonitorRunner",
            file: "server/src/nest/modules/heartbeat/metric-monitor.runner.ts",
            description:
              "A setInterval-based NestJS service that calls runPipeline() every 30 minutes. Not driven by the LangGraph runner — intentionally bypasses it for lower latency and cost.",
          },
        ],
      },
      {
        num: 2,
        title: "Phase 1 — Deterministic KPI Scan",
        operatorView:
          "Each configured KPI is fetched from Looker and compared against its warning and critical thresholds. No AI is used. Cost: ~$0 per healthy run.",
        technical: [
          {
            type: "TOOLBOX TOOL",
            name: "looker-kpi-scanner",
            file: "server/src/toolbox/looker/",
            description:
              "Fetches each KPI's current value from the fct_appointments_table_phi_exclude explore (model: snow_prd_analytics_db) using OAuth client credentials (LOOKER_CLIENT_ID, LOOKER_CLIENT_SECRET from Secrets Manager). Compares against warningValue and criticalValue thresholds configured in the agent seed.",
          },
        ],
      },
      {
        num: 3,
        title: "All healthy → log HeartbeatRun and stop",
        operatorView:
          "If no KPI is in breach, a HeartbeatRun record is logged and the pipeline exits. No email is sent, no AI is called.",
        technical: [
          {
            type: "DATABASE WRITE",
            name: "HeartbeatRun",
            file: "server/src/nest/modules/heartbeat/",
            description:
              "Creates a HeartbeatRun DB record with status=healthy and writes state.json to the agent's data folder. Pipeline stops here — Phase 2 never fires on healthy runs.",
          },
        ],
      },
      {
        num: 4,
        title: "Breach detected → Phase 2 triggers",
        operatorView:
          "When any KPI crosses a threshold, Claude is called with full context: which KPIs breached, by how much, and how similar issues were resolved before.",
        technical: [
          {
            type: "BEDROCK CALL",
            name: "claude-haiku-4-5",
            description:
              "Single Bedrock call with: scan results, agent roster, soul.md contents. Tools available: issue_memory, escalation_ladder, audit_log. IssueMemory provides historical context from previous similar breaches for context-aware routing.",
          },
        ],
      },
      {
        num: 5,
        title: "Claude creates PjEscalation",
        operatorView:
          "Claude decides routing and severity. A PjEscalation record is created in the database and HeartbeatRuns are queued for downstream agents.",
        technical: [
          {
            type: "DATABASE WRITE",
            name: "PjEscalation",
            description:
              "Claude uses issue_memory to recall how similar breaches were resolved, then writes a PjEscalation record using the escalation_ladder tool. Routing decisions are Claude's — not hardcoded rules.",
          },
        ],
      },
      {
        num: 6,
        title: "Board email notification sent",
        operatorView:
          "The board receives an email summarizing the breach, Claude's assessment, and the escalation path.",
        technical: [
          {
            type: "EMAIL",
            name: "Board notification",
            description:
              "Triggered from the PjEscalation creation flow. Summary includes breached KPI values, thresholds, Claude's reasoning, and next steps. Recipients configured per agent config.",
          },
        ],
      },
    ],
  },

  "stage-2": {
    label: "Stage 2 — Initial Appointments",
    agentType: "metric_monitor",
    goal: "Focused monitor for Stage 2 of the patient journey. Independently checks 3 KPIs for initial appointments on the same 30-minute cycle as the umbrella monitor.",
    steps: [
      {
        num: 1,
        title: "Phase 1 — Scan Stage 2 KPIs",
        operatorView:
          "Three KPIs are checked: initial appointment no-show rate, no-show recovery rate, and the percentage of fulfilled initial appointments with no US scheduled.",
        technical: [
          {
            type: "LOOKER QUERY",
            name: "Stage 2 KPI fields",
            file: "server/src/admin/seed/patient-journey.ts",
            description:
              "Initial Appt No-Show Rate → fct_appointments_table_phi_exclude.percent_appointments_no_show, sliced to appointment_type = Vein/Pain Initial (warn >15%, crit >25%). The No-Show Recovery Rate (warn <40%, crit <25%) and Fulfilled Initial w/ No US Scheduled % (warn >20%, crit >35%) KPIs have no backing Looker measure yet. Explore: fct_appointments_table_phi_exclude · model: snow_prd_analytics_db. All gated unsupported pending measure validation.",
          },
        ],
      },
      {
        num: 2,
        title: "Phase 2 (on breach only) — Claude call",
        operatorView:
          "Same breach flow as the umbrella monitor. Claude is only called if a Stage 2 KPI crosses a threshold.",
        technical: [
          {
            type: "BEDROCK CALL",
            name: "claude-haiku-4-5",
            description:
              "Same two-phase flow as the umbrella monitor, scoped to Stage 2 context. Creates a PjEscalation tagged to Stage 2.",
          },
        ],
      },
    ],
  },

  "stage-3": {
    label: "Stage 3 — Ultrasound Review",
    agentType: "metric_monitor",
    goal: "Focused monitor for Stage 3 (ultrasound review). Checks the US Review no-show rate against the real Appointments explore. Scheduling drop-off (us_without_procedure_pct) has no backing Looker measure yet, so it is gated and reports no_data rather than a value.",
    steps: [
      {
        num: 1,
        title: "Phase 1 — Scan Stage 3 KPIs",
        operatorView:
          "The US Review no-show rate is checked against live Looker data. The percentage of fulfilled US appointments with no procedure scheduled has no backing measure yet, so it reports no_data.",
        technical: [
          {
            type: "LOOKER QUERY",
            name: "Stage 3 KPI fields",
            file: "server/src/admin/seed/patient-journey.ts",
            description:
              "US Review No-Show Rate → fct_appointments_table_phi_exclude.percent_appointments_no_show, sliced to vein_sequence = \"2 - FU+US\" (warn >15%, crit >25%). Fulfilled US w/ No Procedure Scheduled % (warn >40%, crit >50%) has no backing Looker measure yet. Explore: fct_appointments_table_phi_exclude · model: snow_prd_analytics_db. Both gated unsupported.",
          },
        ],
      },
      {
        num: 2,
        title: "Phase 2 (on breach only) — Claude call",
        operatorView:
          "Claude is only called if a Stage 3 KPI crosses a threshold. While the KPIs are gated unsupported, Phase 2 does not fire.",
        technical: [
          {
            type: "BEDROCK CALL",
            name: "claude-haiku-4-5",
            description:
              "Same two-phase flow as the umbrella monitor, scoped to Stage 3 context. Creates a PjEscalation tagged to Stage 3 when a backed KPI breaches its threshold.",
          },
        ],
      },
    ],
  },

  "stage-5": {
    label: "Stage 5 — Procedure/Ablation",
    agentType: "metric_monitor",
    goal: "Focused monitor for Stage 5. Tracks the procedure no-show rate independently from the umbrella monitor.",
    steps: [
      {
        num: 1,
        title: "Phase 1 — Scan Stage 5 KPI",
        operatorView: "One KPI is checked: the procedure no-show rate.",
        technical: [
          {
            type: "LOOKER QUERY",
            name: "Stage 5 KPI field",
            file: "server/src/admin/seed/patient-journey.ts",
            description:
              "Procedure No-Show Rate → fct_appointments_table_phi_exclude.percent_appointments_no_show, sliced to appointment_type = Vein/Pain Procedure (warn >10%, crit >20%). Explore: fct_appointments_table_phi_exclude · model: snow_prd_analytics_db. Gated unsupported pending measure validation.",
          },
        ],
      },
      {
        num: 2,
        title: "Phase 2 (on breach only)",
        operatorView:
          "Claude is called only if the procedure no-show rate crosses a threshold.",
        technical: [
          {
            type: "BEDROCK CALL",
            name: "claude-haiku-4-5",
            description:
              "Same two-phase flow scoped to Stage 5. Creates a PjEscalation tagged to Stage 5.",
          },
        ],
      },
    ],
  },

  "triage-router": {
    label: "Triage Router",
    agentType: "router",
    goal: "Routes incoming PjEscalations to the correct remediation agent based on the nature and severity of the breach. First decision point in the remediation path after a monitor fires.",
    steps: [
      {
        num: 1,
        title: "Receive PjEscalation from monitor",
        operatorView:
          "When a KPI breach triggers an escalation, the Triage Router receives it and classifies the issue type.",
        technical: [
          {
            type: "ROUTER AGENT",
            name: "triage-router",
            description:
              "Reads the PjEscalation record. Categorizes by: stage (2/3/5), breach type (no-show, scheduling gap, authorization), severity (warning/critical).",
          },
        ],
      },
      {
        num: 2,
        title: "Route to appropriate downstream agent",
        operatorView:
          "Based on the breach type, the router sends the issue to the right agent: Ops Supervisor for operational issues, Prior Auth Agent for authorization blockers.",
        technical: [
          {
            type: "ROUTING DECISION",
            name: "escalation_ladder tool",
            description:
              "Uses the escalation_ladder tool to determine routing. Operational issues (no-show, scheduling gaps) → ops-supervisor. Authorization blockers → prior-auth-agent. Portal navigation needed → portal-navigator (CUA).",
          },
        ],
      },
    ],
  },

  "ops-supervisor": {
    label: "Ops Supervisor",
    agentType: "supervisor",
    goal: "Oversees the remediation workflow for operational KPI breaches. Coordinates with downstream agents to address no-show rates, scheduling gaps, and stage transition bottlenecks.",
    steps: [
      {
        num: 1,
        title: "Receive operational escalation",
        operatorView:
          "The Ops Supervisor receives a routed escalation for operational issues such as high no-show rates or stage transition gaps.",
        technical: [
          {
            type: "SUPERVISOR AGENT",
            name: "ops-supervisor",
            description:
              "Reads the PjEscalation and full breach context. Queries IssueMemory to review how prior similar escalations were resolved.",
          },
        ],
      },
      {
        num: 2,
        title: "Assess breach and plan remediation",
        operatorView:
          "The supervisor assesses severity and creates a remediation plan, drawing on memory of what worked in previous similar situations.",
        technical: [
          {
            type: "BEDROCK CALL",
            name: "claude-haiku-4-5",
            description:
              "Uses issue_memory to contextualize the breach. Determines if worker agents need to be dispatched and what actions to recommend.",
          },
        ],
      },
      {
        num: 3,
        title: "Dispatch downstream agents",
        operatorView:
          "The supervisor queues tasks for worker agents and schedules follow-up HeartbeatRuns to verify resolution.",
        technical: [
          {
            type: "HEARTBEAT QUEUE",
            name: "HeartbeatRun dispatch",
            description:
              "Queues HeartbeatRuns for relevant downstream worker agents. Tracks remediation state in the PjEscalation record.",
          },
        ],
      },
    ],
  },

  "prior-auth": {
    label: "Prior Auth Agent",
    agentType: "worker",
    goal: "Handles prior authorization-related blockers in the patient journey. Works alongside Portal Navigator to submit and track authorization requests through payer portals.",
    steps: [
      {
        num: 1,
        title: "Receive authorization escalation",
        operatorView:
          "The Prior Auth Agent receives a routed escalation for authorization-related delays — e.g., procedures blocked pending payer approval.",
        technical: [
          {
            type: "WORKER AGENT",
            name: "prior-auth-agent",
            description:
              "Reads the PjEscalation. Determines which payer portal to access and what authorization request to submit or track.",
          },
        ],
      },
      {
        num: 2,
        title: "Navigate payer portal via Portal Navigator",
        operatorView:
          "The agent uses computer use to log into payer portals, look up the case, and submit or track the prior authorization request.",
        technical: [
          {
            type: "COMPUTER USE",
            name: "portal-navigator (CUA)",
            description:
              "Pairs with Portal Navigator (type: computer_use). Portal Navigator navigates payer portal UIs to submit auth requests and retrieve status. Prior Auth Agent supervises and interprets the results.",
          },
        ],
      },
    ],
  },

  "agent-architect": {
    label: "Agent Architect",
    agentType: "general",
    goal: "Scaffolds new agents following the canonical 5-file + data/ + notes/ pattern in a single pass. Ensures every new agent is consistent with the platform's naming and structure conventions.",
    steps: [
      {
        num: 1,
        title: "Read existing agents and conventions",
        operatorView:
          "A human or platform process requests a new agent. The architect reads all existing agents and conventions before generating anything.",
        technical: [
          {
            type: "GENERAL AGENT",
            name: "agent-architect",
            file: "agents/agent-architect/",
            description:
              "Model: claude-opus-4-5. Reads: ARCHITECTURE.md, existing agent folders, NAMING.md. Ensures no naming conflicts and follows established patterns.",
          },
        ],
      },
      {
        num: 2,
        title: "Generate canonical agent folder",
        operatorView:
          "The architect creates the complete agent folder structure in one pass.",
        technical: [
          {
            type: "FILE GENERATION",
            name: "5-file agent scaffold",
            description:
              "Creates: soul.md (identity + instructions), config.json (type, model, schedule), tools.json (tool declarations), CLAUDE.md (context for Claude Code), README.md (human docs), data/ (state + runs), notes/ (Confirmations, Corrections, Errors vaults).",
          },
        ],
      },
    ],
  },

  "data-centralizer": {
    label: "Data Centralizer",
    agentType: "general",
    goal: "The only agent permitted to read across all agents' data/ folders. Normalizes run results and KPI state into a shared schema for cross-agent analytics.",
    steps: [
      {
        num: 1,
        title: "Cross-agent data read",
        operatorView:
          "The Data Centralizer reads the state and run history from every agent on the platform.",
        technical: [
          {
            type: "GENERAL AGENT",
            name: "data-centralizer",
            file: "agents/data-centralizer/",
            description:
              "Model: claude-haiku-4-5. Reads: agents/*/data/state.json, agents/*/data/runs/*.json. Cross-agent read permission is exclusive to this agent.",
          },
        ],
      },
      {
        num: 2,
        title: "Normalize and write to own store",
        operatorView:
          "Run results and KPI states are normalized into a shared schema and stored in the centralizer's own data folder only.",
        technical: [
          {
            type: "FILE WRITE",
            name: "Centralized data store",
            description:
              "Writes to: agents/data-centralizer/data/ only. The centralizer never writes to other agents' folders.",
          },
        ],
      },
    ],
  },

  "upgrade-agent": {
    label: "Upgrade Agent",
    agentType: "general",
    goal: "Audits toolbox tool versions against agent declarations and generates a human-readable upgrade report. Never applies changes automatically — human review is always required.",
    steps: [
      {
        num: 1,
        title: "Audit toolbox vs agent declarations",
        operatorView:
          "The Upgrade Agent reads all toolbox manifests and all agent tool declarations to find version mismatches or deprecated dependencies.",
        technical: [
          {
            type: "GENERAL AGENT",
            name: "upgrade-agent",
            file: "agents/upgrade-agent/",
            description:
              "Model: claude-haiku-4-5. Reads: toolbox/*/tool.json (tool manifests), agents/*/tools.json (agent declarations). Compares versions and flags mismatches.",
          },
        ],
      },
      {
        num: 2,
        title: "Generate upgrade report (human review required)",
        operatorView:
          "A human-readable upgrade report is produced. No changes are applied — an engineer must review and apply any toolbox changes.",
        technical: [
          {
            type: "REPORT OUTPUT",
            name: "Upgrade report",
            description:
              "Report includes: mismatched versions, deprecated tools, recommended upgrade path. All changes require explicit human approval before being applied.",
          },
        ],
      },
    ],
  },

  "memory-audit": {
    label: "Memory Audit Agent",
    agentType: "general",
    goal: "Scans all agents' notes/ vaults for stale learnings, unresolved errors, and contradictions. Keeps agent memory healthy and consistent across the platform.",
    steps: [
      {
        num: 1,
        title: "Read all agent memory vaults",
        operatorView:
          "The Memory Audit Agent reads the notes/ folder of every agent on the platform.",
        technical: [
          {
            type: "GENERAL AGENT",
            name: "memory-audit-agent",
            file: "agents/memory-audit-agent/",
            description:
              "Model: claude-haiku-4-5. Reads: agents/*/notes/ — all Confirmations.md, Corrections.md, and Errors.md vaults across all agents.",
          },
        ],
      },
      {
        num: 2,
        title: "Flag issues and write audit summary",
        operatorView:
          "Stale learnings, contradictions between agents, and unresolved errors are identified and summarized.",
        technical: [
          {
            type: "FILE WRITE",
            name: "Audit summary",
            description:
              "Writes to: agents/memory-audit-agent/data/ only. Flags entries that are stale (>30 days unreviewed), contradicted by newer corrections, or unresolved errors blocking agent runs.",
          },
        ],
      },
    ],
  },

  "model-optimizer": {
    label: "Model Optimizer",
    agentType: "general",
    goal: "Analyzes cost and quality data across all agent runs and recommends model changes per agent type. Human review required — no automatic model switching.",
    steps: [
      {
        num: 1,
        title: "Analyze cost and quality metrics",
        operatorView:
          "The optimizer reads all agent run histories and Bedrock cost data to find over- or under-provisioned agents.",
        technical: [
          {
            type: "GENERAL AGENT",
            name: "model-optimizer",
            file: "agents/model-optimizer/",
            description:
              "Model: claude-opus-4-5. Reads: agents/*/data/runs/*.json, cost metrics from AWS Bedrock (cost per 1M tokens by model). Looks for quality-cost mismatches.",
          },
        ],
      },
      {
        num: 2,
        title: "Generate recommendation report",
        operatorView:
          "A report is produced recommending model upgrades or downgrades per agent. An engineer must review before any model is changed.",
        technical: [
          {
            type: "REPORT OUTPUT",
            name: "Model recommendation report",
            description:
              "Recommends: downgrade if agent is using Opus for routine tasks; upgrade if Haiku is producing low-quality reasoning. All changes require explicit human approval.",
          },
        ],
      },
    ],
  },

  "looker-scanner": {
    label: "Looker KPI Scanner",
    agentType: "toolbox",
    goal: "The core toolbox tool used by all metric monitor agents. Fetches live KPI values from Looker and returns threshold comparison results. Zero LLM cost on healthy runs.",
    steps: [
      {
        num: 1,
        title: "Authenticate to Looker API",
        operatorView:
          "The scanner authenticates using service account credentials injected from AWS Secrets Manager at container startup.",
        technical: [
          {
            type: "TOOLBOX TOOL",
            name: "looker-kpi-scanner",
            file: "server/src/toolbox/looker/",
            description:
              "Uses OAuth2 client credentials flow. Reads LOOKER_CLIENT_ID, LOOKER_CLIENT_SECRET, LOOKER_BASE_URL from environment (injected by ECS task def from Secrets Manager).",
          },
        ],
      },
      {
        num: 2,
        title: "Fetch KPI values from the Appointments explore",
        operatorView:
          "Each configured KPI is queried from the Looker explore backed by NexTech/Snowflake data.",
        technical: [
          {
            type: "LOOKER API",
            name: "fct_appointments_table_phi_exclude",
            description:
              "Explore: fct_appointments_table_phi_exclude. Model: snow_prd_analytics_db (Snowflake). PHI-safe: all views use the _phi_exclude suffix. Patient-journey KPIs read the percent_appointments_no_show measure, sliced per stage via appointment_type and vein_sequence.",
          },
        ],
      },
      {
        num: 3,
        title: "Compare values against thresholds and return",
        operatorView:
          "Each KPI value is compared against its warning and critical thresholds. Results are returned to the calling agent.",
        technical: [
          {
            type: "THRESHOLD COMPARISON",
            name: "KPI threshold config",
            file: "server/src/admin/seed/patient-journey.ts",
            description:
              "Each KPI entry has warningValue, criticalValue, and the Looker lookerField + query objects. Results include: value, status (healthy/warning/critical), and field metadata.",
          },
        ],
      },
    ],
  },

  "metric-runner": {
    label: "MetricMonitorRunner",
    agentType: "service",
    goal: "The NestJS service that drives the entire Patient Journey Monitor pipeline on a fixed 30-minute schedule. Intentionally bypasses the LangGraph runner for lower latency and cost.",
    steps: [
      {
        num: 1,
        title: "Initialize on NestJS server startup",
        operatorView:
          "The MetricMonitorRunner starts automatically when the application boots on ECS.",
        technical: [
          {
            type: "NESTJS SERVICE",
            name: "MetricMonitorRunner",
            file: "server/src/nest/modules/heartbeat/metric-monitor.runner.ts",
            description:
              "Registered as a NestJS provider in the Heartbeat module. Initializes a setInterval loop on application startup.",
          },
        ],
      },
      {
        num: 2,
        title: "Run pipeline every 30 minutes",
        operatorView:
          "The pipeline fires on a fixed schedule, independent of any user action or API request.",
        technical: [
          {
            type: "SCHEDULED LOOP",
            name: "setInterval — 30 min",
            description:
              "Calls runPipeline() every 30 minutes. runPipeline() invokes the looker-kpi-scanner tool and handles the Phase 1 / Phase 2 branch logic based on scan results.",
          },
        ],
      },
      {
        num: 3,
        title: "LangGraph runner bypass (intentional)",
        operatorView:
          "Unlike workflow graphs defined in Workflow Studio, this pipeline does not go through the LangGraph graph runner.",
        technical: [
          {
            type: "ARCHITECTURE NOTE",
            name: "Direct NestJS service calls",
            description:
              "The LangGraph runner is used for graphs defined in Workflow Builder UI. MetricMonitorRunner bypasses it entirely — uses direct NestJS service calls for lower latency, simpler error handling, and lower cost.",
          },
        ],
      },
    ],
  },
};

// ─── System Map ───────────────────────────────────────────────────────────────

const LAYER_COLORS: Record<LayerType, string> = {
  frontend: "bg-violet-50 border-violet-200 dark:bg-violet-950/30 dark:border-violet-800",
  backend: "bg-indigo-50 border-indigo-200 dark:bg-indigo-950/30 dark:border-indigo-800",
  database: "bg-emerald-50 border-emerald-200 dark:bg-emerald-950/30 dark:border-emerald-800",
  analytics: "bg-teal-50 border-teal-200 dark:bg-teal-950/30 dark:border-teal-800",
  ai: "bg-pink-50 border-pink-200 dark:bg-pink-950/30 dark:border-pink-800",
  automation: "bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-800",
  auth: "bg-sky-50 border-sky-200 dark:bg-sky-950/30 dark:border-sky-800",
  infra: "bg-slate-100 border-slate-200 dark:bg-slate-800/40 dark:border-slate-700",
};

const LAYER_TITLE_COLORS: Record<LayerType, string> = {
  frontend: "text-violet-700 dark:text-violet-300",
  backend: "text-indigo-700 dark:text-indigo-300",
  database: "text-emerald-700 dark:text-emerald-300",
  analytics: "text-teal-700 dark:text-teal-300",
  ai: "text-pink-700 dark:text-pink-300",
  automation: "text-amber-700 dark:text-amber-300",
  auth: "text-sky-700 dark:text-sky-300",
  infra: "text-slate-700 dark:text-slate-300",
};

const SYSTEM_CARDS: {
  name: string;
  description: string;
  infra: string;
  layer: LayerType;
}[] = [
  {
    name: "React Frontend",
    description:
      "Everything you see in the browser — dashboards, Agent Studio, Workflow Builder, docs, and this page",
    infra: "React + Vite/Rspack · Tailwind · ReactFlow",
    layer: "frontend",
  },
  {
    name: "NestJS API",
    description:
      "Express hybrid backend — handles auth, agent orchestration, heartbeat pipeline, and all REST endpoints",
    infra: "ECS Fargate · Better Auth v1.4.18 · Express hybrid",
    layer: "backend",
  },
  {
    name: "PostgreSQL (RDS)",
    description:
      "Main database — every agent, run, escalation, user session, and heartbeat record lives here",
    infra: "AWS RDS · ligament-health-sandbox · db: ligament",
    layer: "database",
  },
  {
    name: "Looker",
    description:
      "KPI analytics source — all patient journey metrics are queried via the fct_appointments_table_phi_exclude explore",
    infra: "snow_prd_analytics_db (Snowflake) · fct_appointments_table_phi_exclude",
    layer: "analytics",
  },
  {
    name: "AWS Bedrock",
    description:
      "AI inference layer — Claude Haiku runs metric monitor Phase 2 reasoning; Opus runs platform agents",
    infra: "claude-haiku-4-5 (monitors) · claude-opus-4-5 (platform agents)",
    layer: "ai",
  },
  {
    name: "LangGraph",
    description:
      "Workflow graph orchestration — runs graphs defined in Workflow Studio. Not used by MetricMonitorRunner",
    infra: "packages/agent-runtime/ · @langchain/langgraph",
    layer: "automation",
  },
  {
    name: "MetricMonitorRunner",
    description:
      "30-minute setInterval pipeline engine — drives Phase 1 KPI scans and Phase 2 breach escalations",
    infra: "server/src/nest/modules/heartbeat/ · NestJS service",
    layer: "automation",
  },
  {
    name: "Cognito + Better Auth",
    description:
      "User authentication — Cognito SSO (Google) via Better Auth's genericOAuth plugin",
    infra: "Better Auth v1.4.18 · storeStateStrategy: cookie · rateLimit: disabled",
    layer: "auth",
  },
  {
    name: "AWS Secrets Manager",
    description:
      "Credential store — Looker API keys, DB password, and all sensitive env vars injected at runtime",
    infra: "LOOKER_CLIENT_ID · LOOKER_CLIENT_SECRET · DB_PASSWORD · us-east-1",
    layer: "infra",
  },
  {
    name: "ECR + ECS Fargate",
    description:
      "Container hosting — NestJS API and React frontend bundled into one Docker image, deployed on ECS",
    infra: "ligament-health-sandbox cluster · us-east-1 · linux/amd64",
    layer: "infra",
  },
];

const LEGEND: { label: string; layer: LayerType }[] = [
  { label: "Frontend", layer: "frontend" },
  { label: "Backend", layer: "backend" },
  { label: "Database", layer: "database" },
  { label: "Analytics", layer: "analytics" },
  { label: "AI / Bedrock", layer: "ai" },
  { label: "Automation", layer: "automation" },
  { label: "Auth", layer: "auth" },
  { label: "Infra", layer: "infra" },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

function TechBlockCard({ block }: { block: TechBlock }) {
  return (
    <div className="rounded-md border bg-muted/30 p-3 space-y-1.5">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] font-semibold tracking-wider text-muted-foreground uppercase">
          {block.type}
        </span>
        <code className="text-xs font-mono font-semibold text-foreground">{block.name}</code>
      </div>
      {block.file && (
        <p className="text-[11px] text-muted-foreground font-mono leading-relaxed">
          File: {block.file}
        </p>
      )}
      <p className="text-xs text-muted-foreground leading-relaxed">{block.description}</p>
    </div>
  );
}

function AgentFlowsView({
  selected,
  onSelect,
}: {
  selected: string;
  onSelect: (id: string) => void;
}) {
  const flow = FLOWS[selected];

  return (
    <div className="flex border rounded-lg overflow-hidden" style={{ minHeight: "72vh" }}>
      {/* Left nav */}
      <div className="w-52 flex-shrink-0 border-r bg-muted/20">
        <div className="p-2 space-y-4 py-3">
          {NAV_SECTIONS.map((section) => (
            <div key={section.label} className="space-y-0.5">
              <p className="px-2 py-1 text-[10px] font-semibold tracking-wider text-muted-foreground uppercase">
                {section.label}
              </p>
              {section.items.map((item) => (
                <button
                  key={item.id}
                  onClick={() => onSelect(item.id)}
                  className={cn(
                    "w-full text-left px-3 py-1.5 rounded-md text-sm transition-colors",
                    selected === item.id
                      ? "bg-primary text-primary-foreground font-medium"
                      : "text-foreground hover:bg-muted",
                  )}
                >
                  {item.label}
                </button>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Right content */}
      <div className="flex-1 p-6 space-y-5 overflow-y-auto">
        {flow ? (
          <>
            {/* GOAL */}
            <div className="pl-4 border-l-[3px] border-l-primary py-2 space-y-1">
              <p className="text-[10px] font-semibold tracking-widest text-primary uppercase">
                Goal
              </p>
              <p className="text-sm text-foreground leading-relaxed">{flow.goal}</p>
            </div>

            {/* Steps */}
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-foreground">What happens, step by step</h3>

              <div className="border rounded-lg overflow-hidden divide-y">
                {/* Column headers */}
                <div className="grid grid-cols-2 bg-muted/40">
                  <div className="px-4 py-2 text-[10px] font-semibold tracking-wider text-muted-foreground uppercase">
                    Operator View (Plain English)
                  </div>
                  <div className="px-4 py-2 text-[10px] font-semibold tracking-wider text-muted-foreground uppercase border-l">
                    Technical Details
                  </div>
                </div>

                {/* Step rows */}
                {flow.steps.map((step) => (
                  <div key={step.num} className="grid grid-cols-2">
                    {/* Operator side */}
                    <div className="px-4 py-4 space-y-2">
                      <div className="flex items-start gap-2">
                        <span className="flex-shrink-0 mt-0.5 w-5 h-5 rounded-full bg-primary/10 text-primary text-[11px] font-bold flex items-center justify-center">
                          {step.num}
                        </span>
                        <p className="text-xs font-semibold text-foreground leading-relaxed">
                          {step.title}
                        </p>
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed pl-7">
                        {step.operatorView}
                      </p>
                    </div>

                    {/* Technical side */}
                    <div className="px-4 py-4 border-l space-y-2 bg-muted/5">
                      {step.technical.map((block, i) => (
                        <TechBlockCard key={i} block={block} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">
            Select an item from the left to view details.
          </p>
        )}
      </div>
    </div>
  );
}

function SystemMapView() {
  return (
    <div className="space-y-6">
      <div className="space-y-0.5">
        <h2 className="text-base font-semibold text-foreground">How the pieces connect</h2>
        <p className="text-xs text-muted-foreground">
          Maintained by: packages/agent-runtime/ · infra/
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {SYSTEM_CARDS.map((card) => (
          <div
            key={card.name}
            className={cn("rounded-lg border p-4 space-y-2", LAYER_COLORS[card.layer])}
          >
            <p className={cn("text-sm font-semibold", LAYER_TITLE_COLORS[card.layer])}>
              {card.name}
            </p>
            <p className="text-xs text-foreground/70 leading-relaxed">{card.description}</p>
            <code className="text-[11px] font-mono text-foreground/50 block">{card.infra}</code>
          </div>
        ))}
      </div>

      {/* Layer legend */}
      <div className="space-y-2 pt-2">
        <p className="text-[10px] font-semibold tracking-wider text-muted-foreground uppercase">
          Layer Legend
        </p>
        <div className="flex flex-wrap gap-2">
          {LEGEND.map(({ label, layer }) => (
            <div
              key={label}
              className={cn(
                "rounded-md border px-3 py-1 text-xs font-medium",
                LAYER_COLORS[layer],
                LAYER_TITLE_COLORS[layer],
              )}
            >
              {label}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function TechWorkflow() {
  const { setBreadcrumbs } = useBreadcrumbs();
  const [view, setView] = useState<"flows" | "map">("flows");
  const [selected, setSelected] = useState("pipeline");

  useEffect(() => {
    setBreadcrumbs([{ label: "System Blueprint" }]);
  }, [setBreadcrumbs]);

  return (
    <div className="space-y-4">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="space-y-0.5">
          <h1 className="text-xl font-semibold text-foreground">System Blueprint</h1>
          <p className="text-sm text-muted-foreground">
            How the Ligament platform is built and how its pieces connect
          </p>
        </div>

        {/* View toggle */}
        <div className="flex items-center gap-0.5 rounded-md bg-muted p-1">
          <button
            onClick={() => setView("flows")}
            className={cn(
              "px-3 py-1.5 rounded-sm text-sm font-medium transition-colors",
              view === "flows"
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            Agent Flows
          </button>
          <button
            onClick={() => setView("map")}
            className={cn(
              "px-3 py-1.5 rounded-sm text-sm font-medium transition-colors",
              view === "map"
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            System Map
          </button>
        </div>
      </div>

      {/* Content */}
      {view === "flows" ? (
        <AgentFlowsView selected={selected} onSelect={setSelected} />
      ) : (
        <SystemMapView />
      )}
    </div>
  );
}
